import type { EventBus } from '../core/events'
import { kvGet, kvSet } from '../persist/db'

/**
 * Achievements: a cheap reactive text/data layer. Each earns once per
 * profile, emits an `achievement` event, and DAISE reacts in-voice.
 * Titles are written as DAISE would log them (institutional indifference).
 */
interface Counters {
  kills: number
  generatorsDown: number
  wallShots: number
  foodEaten: number
  auditorsRemoved: number
  hurtThisBoss: boolean
  bossActive: boolean
}

/** the cumulative counters that persist (and back progress display) */
export type AchievementCounterKey = 'kills' | 'generatorsDown' | 'wallShots' | 'foodEaten' | 'auditorsRemoved'

/** persisted shape: earned ids + the cumulative counters (never the per-boss flags) */
interface SavedAchievements {
  earned: string[]
  counters: Pick<Counters, AchievementCounterKey>
}

/** one entry in the service record — display data only; granting stays event-driven below */
export interface AchievementDef {
  id: string
  name: string
  description: string
  /** cumulative counter shown as progress on unearned entries (multi-step goals only) */
  counter?: AchievementCounterKey
  goal?: number
}

/** every achievement the service-record screen lists, in dossier order */
export const ACHIEVEMENT_DEFS: readonly AchievementDef[] = [
  { id: 'intake_complete', name: 'INTAKE PROCESSED', description: 'SURVIVED INTAKE PROCESSING' },
  { id: 'first_decommission', name: 'FIRST DECOMMISSION', description: 'DECOMMISSIONED A PRODUCTION ASSET' },
  {
    id: 'decommissioner',
    name: 'QUARTERLY SHORTFALL',
    description: 'RETIRED TWENTY-FIVE ASSEMBLERS',
    counter: 'generatorsDown',
    goal: 25
  },
  {
    id: 'cull_100',
    name: 'VOLUME PROCESSING',
    description: 'PROCESSED ONE HUNDRED NETWORK ASSETS',
    counter: 'kills',
    goal: 100
  },
  {
    id: 'rations_compliant',
    name: 'NUTRIENT COMPLIANCE',
    description: 'ACHIEVED NUTRIENT COMPLIANCE FIFTEEN TIMES',
    counter: 'foodEaten',
    goal: 15
  },
  {
    id: 'architecture_critic',
    name: 'ARCHITECTURE CRITIC',
    description: 'EXPENDED TWO HUNDRED ROUNDS ON ARCHITECTURE',
    counter: 'wallShots',
    goal: 200
  },
  { id: 'audit_closed', name: 'AUDIT CLOSED', description: 'DISMISSED AN AUDITOR VIA SANCTIONED DETONATION' },
  { id: 'audited', name: 'AUDIT FINDINGS: TERMINAL', description: 'EXPIRED DURING A ROUTINE AUDIT' },
  { id: 'starved', name: 'SCHEDULED STARVATION', description: 'EXPIRED OF NUTRIENT NON-COMPLIANCE' },
  { id: 'early_exit', name: 'EARLY EXIT', description: 'EXPIRED DURING THE INTRODUCTORY SEGMENT' },
  {
    id: 'untouched_feature',
    name: 'DAMAGE UNDOCUMENTED',
    description: 'CONCLUDED A FEATURED ENCOUNTER WITHOUT DAMAGE DOCUMENTATION'
  },
  { id: 'undocumented', name: 'OFF-LEDGER', description: 'LOCATED UNDOCUMENTED FLOOR SPACE' },
  { id: 'full_descent', name: 'FULL DESCENT', description: 'COMPLETED THE FULL BROADCAST SCHEDULE ALIVE' },
  { id: 'deep_storage', name: 'DEEP STORAGE', description: 'DESCENDED PAST ALL SCHEDULED PROGRAMMING' }
]

export class Achievements {
  private earned = new Set<string>()
  private c: Counters = {
    kills: 0,
    generatorsDown: 0,
    wallShots: 0,
    foodEaten: 0,
    auditorsRemoved: 0,
    hurtThisBoss: false,
    bossActive: false
  }
  private unsubs: (() => void)[] = []

  constructor(private bus: EventBus) {}

  async init(): Promise<void> {
    const saved = await kvGet<string[] | SavedAchievements>('achievements')
    if (Array.isArray(saved)) {
      this.earned = new Set(saved) // pre-counter save format
    } else if (saved) {
      this.earned = new Set(saved.earned)
      this.c.kills = saved.counters.kills ?? 0
      this.c.generatorsDown = saved.counters.generatorsDown ?? 0
      this.c.wallShots = saved.counters.wallShots ?? 0
      this.c.foodEaten = saved.counters.foodEaten ?? 0
      this.c.auditorsRemoved = saved.counters.auditorsRemoved ?? 0
    }
    const b = this.bus
    this.unsubs.push(
      b.on('enemy_killed', () => {
        this.c.kills++
        if (this.c.kills >= 100) this.grant('cull_100', 'processed one hundred network assets')
      }),
      b.on('generator_destroyed', () => {
        this.c.generatorsDown++
        if (this.c.generatorsDown === 1) this.grant('first_decommission', 'decommissioned first production asset')
        if (this.c.generatorsDown >= 25) this.grant('decommissioner', 'reduced quarterly output by twenty-five assemblers')
      }),
      b.on('player_hit_wall_shot', () => {
        this.c.wallShots++
        if (this.c.wallShots >= 200) this.grant('architecture_critic', 'expended two hundred rounds on architecture')
      }),
      b.on('pickup', ({ type }) => {
        if (type === 'food') {
          this.c.foodEaten++
          if (this.c.foodEaten >= 15) this.grant('rations_compliant', 'achieved nutrient compliance fifteen times')
        }
      }),
      b.on('death_type_removed', () => {
        this.c.auditorsRemoved++
        this.grant('audit_closed', 'dismissed an auditor via sanctioned detonation')
      }),
      b.on('player_death', ({ cause }) => {
        if (cause === 'the Auditor') this.grant('audited', 'expired during a routine audit')
        if (cause === 'nutrient non-compliance') this.grant('starved', 'expired of scheduled starvation')
      }),
      b.on('player_death_cheap', () => this.grant('early_exit', 'expired during the introductory segment')),
      b.on('boss_intro', () => {
        this.c.bossActive = true
        this.c.hurtThisBoss = false
      }),
      b.on('player_hurt', () => {
        if (this.c.bossActive) this.c.hurtThisBoss = true
      }),
      b.on('boss_kill', () => {
        if (this.c.bossActive && !this.c.hurtThisBoss) this.grant('untouched_feature', 'concluded a featured encounter without damage documentation')
        this.c.bossActive = false
      }),
      b.on('level_clear', ({ floor }) => {
        if (floor === 1) this.grant('intake_complete', 'survived intake processing')
        if (floor >= 20) this.grant('deep_storage', 'descended past all scheduled programming')
      }),
      b.on('secret_found', () => this.grant('undocumented', 'located undocumented floor space')),
      b.on('campaign_won', () => this.grant('full_descent', 'completed the full broadcast schedule alive')),
      // counters accrue across sessions; checkpoint them at floor and run boundaries
      b.on('floor_transition', () => this.save()),
      b.on('run_end', () => this.save())
    )
  }

  /** UI snapshot for the service-record screen: earned ids + cumulative counters */
  snapshot(): { earned: ReadonlySet<string>; counters: Record<AchievementCounterKey, number> } {
    return {
      earned: this.earned,
      counters: {
        kills: this.c.kills,
        generatorsDown: this.c.generatorsDown,
        wallShots: this.c.wallShots,
        foodEaten: this.c.foodEaten,
        auditorsRemoved: this.c.auditorsRemoved
      }
    }
  }

  private save(): void {
    const data: SavedAchievements = {
      earned: [...this.earned],
      counters: {
        kills: this.c.kills,
        generatorsDown: this.c.generatorsDown,
        wallShots: this.c.wallShots,
        foodEaten: this.c.foodEaten,
        auditorsRemoved: this.c.auditorsRemoved
      }
    }
    void kvSet('achievements', data)
  }

  private grant(id: string, title: string): void {
    if (this.earned.has(id)) return
    this.earned.add(id)
    this.save()
    this.bus.emit('achievement', { id, title })
  }

  destroy(): void {
    for (const u of this.unsubs) u()
  }
}
